import React, { useState, useEffect } from "react";
import { GoTrash } from "react-icons/go";
import { IoMdAdd } from "react-icons/io";
import { FiSearch, FiGrid, FiList, FiStar } from "react-icons/fi";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import PageHeader from "../../../components/ui/PageHeader";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import Button from "../../../components/ui/Button";
import { strings } from "../../../strings/strings";
import ReviewModal from "./ReviewModal";
import ReviewCard from "./ReviewCard";
import ReviewTable from "./ReviewTable";
import { apiCall } from "../../../api/fetchClient";
import { 
  GET_REVIEW_LIST_URL, 
  DELETE_REVIEW_URL,
  TOGGLE_REVIEW_STATUS_URL
} from "../../../api/url";

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // 'table' or 'grid'
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, review: null });

  const fetchReviews = async () => {
    setIsLoading(true);
    try {
      const response = await apiCall(GET_REVIEW_LIST_URL, "GET", null, false, false);
      if (response?.results?.data) {
        setReviews(response.results.data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const filteredReviews = Array.isArray(reviews) 
    ? reviews.filter(r => 
        (r.name && r.name.toLowerCase().includes(searchQuery.toLowerCase())) || 
        (r.companyName && r.companyName.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : [];

  const handleAdd = () => {
    setSelectedReview(null);
    setIsModalOpen(true);
  };

  const handleEdit = (review) => {
    setSelectedReview(review);
    setIsModalOpen(true);
  };

  const handleDelete = (review) => {
    setConfirmModal({ isOpen: true, review });
  };

  const confirmDelete = async () => {
    try {
      const response = await apiCall(`${DELETE_REVIEW_URL}${confirmModal.review._id || confirmModal.review.id}`, "DELETE", null, false, true);
      if (response?.success) {
        fetchReviews();
      }
    } catch (error) {
    } finally {
      setConfirmModal({ isOpen: false, review: null });
    }
  };

  const handleToggleStatus = async (review) => {
    try {
      const response = await apiCall(`${TOGGLE_REVIEW_STATUS_URL}${review._id || review.id}`, "PUT", null, false, true);
      if (response?.success) {
        fetchReviews();
      }
    } catch (error) {
    }
  };

  const handleSave = () => {
    fetchReviews();
    setIsModalOpen(false);
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title={strings.REVIEWS_MANAGEMENT}
        description={strings.REVIEWS_DESCRIPTION}
        icon={FiStar}
      >
        <Button onClick={handleAdd}>
          <IoMdAdd className="w-4 h-4 mr-2" />
          {strings.ADD_REVIEW}
        </Button>
      </PageHeader>

      <div className="mb-6 bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm border border-gray-100 dark:border-slate-800 flex flex-wrap gap-4 items-center justify-between">
        <div className="relative w-full sm:w-auto sm:min-w-[300px]">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={strings.SEARCH_REVIEWS}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-800 dark:text-white"
          />
        </div>
        
        <div className="flex bg-gray-100 dark:bg-slate-800 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('table')}
            className={`p-2 rounded-md flex items-center transition-colors ${
              viewMode === 'table' 
                ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' 
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
            title="Table View"
          >
            <FiList className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md flex items-center transition-colors ${
              viewMode === 'grid' 
                ? 'bg-white dark:bg-slate-700 text-primary shadow-sm' 
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
            title="Grid View"
          >
            <FiGrid className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner message="Loading reviews..." minHeight="h-64" />
      ) : filteredReviews.length > 0 ? (
        viewMode === 'table' ? (
          <ReviewTable 
            reviews={filteredReviews} 
            onEdit={handleEdit} 
            onDelete={handleDelete} 
            onToggleStatus={handleToggleStatus}
          />
        ) : (
          <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
            {filteredReviews.map((review) => (
              <ReviewCard 
                key={review._id || review.id} 
                review={review} 
                onEdit={handleEdit} 
                onDelete={handleDelete} 
                onToggleStatus={handleToggleStatus}
              />
            ))}
          </div>
        )
      ) : (
        <div className="flex flex-col items-center justify-center h-64 bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800">
          <p className="text-gray-500 dark:text-gray-400">{strings.NO_REVIEWS_YET}</p>
          <Button onClick={handleAdd} className="mt-4">
            {strings.ADD_REVIEW}
          </Button>
        </div>
      )}

      <ReviewModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        review={selectedReview}
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, review: null })}
        onConfirm={confirmDelete}
        title={strings.DELETE_REVIEW_TITLE}
        message={confirmModal.review ? strings.DELETE_REVIEW_MESSAGE(confirmModal.review.name) : ""}
        confirmText={strings.DELETE}
        variant="danger"
        icon={GoTrash}
      />
    </div>
  );
};

export default ReviewsPage;
